import { useEffect, useState, useRef } from "react";
import {
  Pencil,
  Trash2,
  Plus,
  Loader2,
  Save,
  X,
  Upload,
} from "lucide-react";
import api, { apiErrorMessage, imageUrl } from "@/lib/api";

// ============== Generic UI bits (re-used) ==============
export function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4 py-8 bg-black/50 backdrop-blur-sm">
      <div className="bg-white w-full max-w-lg rounded-xl shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-tnt-border">
          <h3 className="font-display text-tnt-ink text-lg tracking-tight">{title}</h3>
          <button type="button" onClick={onClose} className="w-8 h-8 inline-flex items-center justify-center rounded-md hover:bg-tnt-surface text-tnt-ink/60 hover:text-tnt-ink">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

export function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="font-display tracking-widest text-[11px] uppercase text-tnt-ink/70">{label}</span>
      <div className="mt-1.5">{children}</div>
      {hint && <p className="mt-1 text-xs text-tnt-ink/50">{hint}</p>}
    </label>
  );
}

export function FormError({ children }) {
  return <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2">{children}</p>;
}

export function FormActions({ onCancel, saving }) {
  return (
    <div className="flex items-center justify-end gap-2 pt-2">
      <button type="button" data-testid="form-cancel" onClick={onCancel} className="px-4 py-2 border border-tnt-border text-tnt-ink/80 hover:text-tnt-ink rounded-md text-sm">Cancel</button>
      <button type="submit" data-testid="form-save" disabled={saving} className="btn-gold !py-2 !px-4 !text-sm disabled:opacity-60">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Save</>}
      </button>
    </div>
  );
}

export function Loading() {
  return <div className="py-16 flex items-center justify-center text-tnt-ink/50"><Loader2 className="w-5 h-5 animate-spin" /></div>;
}

export function EmptyState({ title, subtitle }) {
  return (
    <div className="py-14 px-6 text-center border border-dashed border-tnt-border rounded-xl bg-white/50">
      <h3 className="font-display text-tnt-ink text-lg">{title}</h3>
      <p className="text-tnt-ink/55 text-sm mt-1">{subtitle}</p>
    </div>
  );
}

// Image picker — used by managers that need to upload via /api/admin/upload first.
export function ImagePicker({ imageUrlState, setImageUrlState }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/api/admin/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      setImageUrlState(data.image_url);
    } catch (err) {
      alert(apiErrorMessage(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  return (
    <div>
      {imageUrlState ? (
        <div className="space-y-2">
          <img src={imageUrl(imageUrlState)} alt="" className="w-full max-h-56 object-contain rounded bg-tnt-surface2" />
          <div className="flex gap-2">
            <button type="button" onClick={() => fileRef.current?.click()} className="text-xs font-display tracking-widest uppercase text-tnt-ink hover:text-tnt-gold">Replace</button>
            <button type="button" onClick={() => setImageUrlState("")} className="text-xs font-display tracking-widest uppercase text-red-600 hover:text-red-700">Remove</button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => fileRef.current?.click()} className="w-full border border-dashed border-tnt-border bg-white hover:bg-tnt-surface rounded-md py-6 text-tnt-ink/65 hover:text-tnt-ink transition-colors inline-flex flex-col items-center justify-center gap-2">
          {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
          <span className="text-sm">{uploading ? "Uploading…" : "Choose photo"}</span>
        </button>
      )}
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPick} />
    </div>
  );
}

// ============== MENU CATEGORIES (fully editable) ==============
export function MenuCategoriesManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/menu-categories");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this category? Items previously assigned to it will be hidden until reassigned.")) return;
    try {
      await api.delete(`/api/admin/menu-categories/${id}`);
      setItems((arr) => arr.filter((e) => e.id !== id));
    } catch (err) {
      alert(apiErrorMessage(err));
    }
  };

  return (
    <section data-testid="admin-menu-categories" className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl text-tnt-ink tracking-tight">
          Menu Categories ({items.length})
        </h2>
        <button
          type="button"
          data-testid="admin-category-add"
          onClick={() => setEditing({ id: null, slug: "", title: "", subtitle: "", image_url: "", order: items.length, visible: true })}
          className="btn-gold !py-2 !px-4 !text-sm"
        >
          <Plus className="w-4 h-4 mr-1.5" /> New category
        </button>
      </div>
      <p className="text-tnt-ink/55 text-xs">
        These tabs power the public Menu page. Drag the <strong>Order</strong> number to reorder. Hide a category to keep its items but remove the tab.
      </p>

      {loading ? <Loading /> : items.length === 0 ? (
        <EmptyState title="No categories yet" subtitle="Add your first category (e.g. Appetizers, Burgers, Drinks)." />
      ) : (
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((c) => (
            <li key={c.id} data-testid={`admin-category-${c.id}`} className="bg-white border border-tnt-border rounded-lg overflow-hidden shadow-sm">
              {c.image_url ? (
                <img src={imageUrl(c.image_url)} alt={c.title} loading="lazy" className="w-full aspect-[4/3] object-cover bg-tnt-surface2" />
              ) : (
                <div className="w-full aspect-[4/3] flex items-center justify-center bg-tnt-surface2 text-tnt-ink/40 text-xs">No banner</div>
              )}
              <div className="p-4">
                <div className="flex items-baseline justify-between">
                  <h3 className="font-display text-lg text-tnt-ink leading-tight">{c.title}</h3>
                  {!c.visible && <span className="font-display tracking-widest uppercase text-[9px] bg-tnt-surface2 text-tnt-ink/60 px-2 py-0.5 rounded">Hidden</span>}
                </div>
                <p className="font-display text-tnt-gold text-[10px] tracking-[0.3em] uppercase mt-1">/{c.slug}</p>
                {c.subtitle && <p className="text-tnt-ink/60 text-xs mt-1 line-clamp-2">{c.subtitle}</p>}
                <p className="text-tnt-ink/50 text-[11px] mt-1">Order: {c.order}</p>
                <div className="mt-3 flex gap-2">
                  <button type="button" data-testid={`admin-category-edit-${c.id}`} onClick={() => setEditing(c)} className="inline-flex items-center gap-1 text-tnt-ink hover:text-tnt-gold text-xs font-display tracking-widest uppercase">
                    <Pencil className="w-3 h-3" /> Edit
                  </button>
                  <button type="button" data-testid={`admin-category-delete-${c.id}`} onClick={() => remove(c.id)} className="ml-auto inline-flex items-center gap-1 text-red-600 hover:text-red-700 text-xs font-display tracking-widest uppercase">
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <CategoryEditor
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </section>
  );
}

function CategoryEditor({ item, onClose, onSaved }) {
  const [state, setState] = useState({ ...item });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const isEdit = Boolean(item.id);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = {
        slug: state.slug || state.title,
        title: state.title,
        subtitle: state.subtitle || "",
        image_url: state.image_url || "",
        order: Number(state.order) || 0,
        visible: state.visible !== false,
      };
      if (isEdit) {
        await api.put(`/api/admin/menu-categories/${item.id}`, payload);
      } else {
        await api.post("/api/admin/menu-categories", payload);
      }
      onSaved();
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={isEdit ? "Edit category" : "New category"} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Name" hint="Shown as the tab label on the Menu page.">
          <input type="text" required data-testid="category-form-title" value={state.title || ""} onChange={(e) => setState({ ...state, title: e.target.value })} className="form-input" placeholder="e.g. Appetizers" />
        </Field>
        <Field label="Slug (optional)" hint="URL-safe ID. Leave blank to auto-generate from the name.">
          <input type="text" data-testid="category-form-slug" value={state.slug || ""} onChange={(e) => setState({ ...state, slug: e.target.value })} className="form-input" placeholder="appetizers" />
        </Field>
        <Field label="Subtitle (optional)">
          <input type="text" data-testid="category-form-subtitle" value={state.subtitle || ""} onChange={(e) => setState({ ...state, subtitle: e.target.value })} className="form-input" placeholder="Shareable starters & crispy classics" />
        </Field>
        <Field label="Display order" hint="Lower numbers appear first.">
          <input type="number" data-testid="category-form-order" value={state.order ?? 0} onChange={(e) => setState({ ...state, order: e.target.value })} className="form-input w-32" />
        </Field>
        <Field label="Visible?" hint="Untick to hide this tab from the public Menu without losing items.">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" data-testid="category-form-visible" checked={state.visible !== false} onChange={(e) => setState({ ...state, visible: e.target.checked })} className="w-4 h-4 accent-tnt-gold" />
            <span className="text-sm text-tnt-ink/80">Show on public menu</span>
          </label>
        </Field>
        <Field label="Banner photo (optional)" hint="Featured image shown at the top of this category on the Menu page.">
          <ImagePicker imageUrlState={state.image_url || ""} setImageUrlState={(url) => setState({ ...state, image_url: url })} />
        </Field>
        {error && <FormError>{error}</FormError>}
        <FormActions onCancel={onClose} saving={saving} />
      </form>
    </Modal>
  );
}

// Hook so MenuItems / MenuPhotos managers always show the current category list.
function useMenuCategoryOptions() {
  const [opts, setOpts] = useState([]);
  useEffect(() => {
    api.get("/api/menu-categories").then((res) => {
      const arr = Array.isArray(res.data) ? res.data : [];
      setOpts(arr.map((c) => ({ value: c.slug, label: c.title })));
    }).catch(() => setOpts([]));
  }, []);
  return opts;
}

// ============== WEEKLY EVENTS ==============
export function WeeklyEventsManager() {
  return <Crud
    endpoint="/api/weekly-events"
    label="Weekly Schedule (Tue–Sun)"
    blurb="Edit the night-by-night cards on the homepage (This Week at TNT)."
    blankItem={{ day: "", title: "", time: "", desc: "", image_url: "", order: 0 }}
    renderCard={(w) => (<><p className="font-display text-tnt-gold text-[10px] tracking-[0.3em] uppercase">{w.day}</p><h3 className="font-display text-lg text-tnt-ink mt-1 leading-tight">{w.title}</h3><p className="text-tnt-ink/60 text-xs mt-1">{w.time} · Order {w.order}</p></>)}
    fields={[
      ["day", "Day label", "text", { placeholder: "Every Tuesday" }],
      ["title", "Title", "text", { required: true }],
      ["time", "Time", "text", { placeholder: "8:30 PM" }],
      ["desc", "Description", "textarea"],
      ["order", "Display order", "number"],
      ["image_url", "Card photo", "image"],
    ]}
  />;
}

// ============== SIGNATURE DISHES ==============
export function DishesManager() {
  return <Crud
    endpoint="/api/signature-dishes"
    label="Eat, Drink & Stay Late"
    blurb="Featured dishes shown on the homepage food preview."
    blankItem={{ name: "", blurb: "", image_url: "", order: 0 }}
    renderCard={(d) => (<><h3 className="font-display text-lg text-tnt-ink leading-tight">{d.name}</h3><p className="text-tnt-ink/60 text-xs mt-1 line-clamp-2">{d.blurb}</p></>)}
    fields={[
      ["name", "Name", "text", { required: true }],
      ["blurb", "Short blurb", "text"],
      ["order", "Display order", "number"],
      ["image_url", "Photo", "image"],
    ]}
  />;
}

// ============== MENU ITEMS ==============
export function MenuItemsManager() {
  const cats = useMenuCategoryOptions();
  const catLabel = (slug) => cats.find((c) => c.value === slug)?.label || slug;
  return <Crud
    endpoint="/api/menu-items"
    label="Menu Items"
    blurb="Each public menu item lives here. Upload or replace its photo, edit description / price, change category, hide it temporarily, or delete it."
    blankItem={{ name: "", price: "", description: "", category: cats[0]?.value || "", image_url: "", order: 0, visible: true }}
    renderCard={(m) => (
      <>
        <div className="flex items-center justify-between">
          <p className="font-display text-tnt-gold text-[10px] tracking-[0.3em] uppercase">{catLabel(m.category)}</p>
          {m.visible === false && (
            <span className="font-display tracking-widest uppercase text-[9px] bg-tnt-surface2 text-tnt-ink/60 px-2 py-0.5 rounded">Hidden</span>
          )}
        </div>
        <div className="flex items-baseline justify-between mt-0.5">
          <h3 className="font-display text-lg text-tnt-ink leading-tight truncate pr-2">{m.name}</h3>
          {m.price && <span className="font-display text-tnt-gold text-sm">{m.price}</span>}
        </div>
        {m.description && <p className="text-tnt-ink/60 text-xs mt-1 line-clamp-2">{m.description}</p>}
      </>
    )}
    fields={[
      ["name", "Name", "text", { required: true }],
      ["price", "Price", "text", { placeholder: "$14" }],
      ["category", "Category", "select", { options: cats }],
      ["description", "Description", "textarea"],
      ["image_url", "Photo (upload or replace)", "image", { hint: "Click to upload a photo for this item. Shown right under its description on the public Menu page." }],
      ["visible", "Show on public menu?", "checkbox", { checkboxLabel: "Visible (untick to temporarily hide without deleting)" }],
      ["order", "Display order", "number", { hint: "Lower numbers appear first within the category." }],
    ]}
  />;
}

// ============== SPECIALS ==============
const SPECIAL_KINDS = [
  { value: "daily", label: "Daily" },
  { value: "food", label: "Food" },
  { value: "drink", label: "Drink" },
];

export function SpecialsManager() {
  return <Crud
    endpoint="/api/specials"
    label="Specials"
    blurb="Edit daily/food/drink specials. Appears in a Specials section on the Menu page."
    blankItem={{ title: "", description: "", kind: "daily", price: "", day_of_week: "", order: 0 }}
    renderCard={(s) => (<><p className="font-display text-tnt-gold text-[10px] tracking-[0.3em] uppercase">{s.kind.toUpperCase()}{s.day_of_week && ` · ${s.day_of_week}`}</p><div className="flex items-baseline justify-between mt-0.5"><h3 className="font-display text-lg text-tnt-ink leading-tight truncate pr-2">{s.title}</h3>{s.price && <span className="font-display text-tnt-gold text-sm">{s.price}</span>}</div>{s.description && <p className="text-tnt-ink/60 text-xs mt-1 line-clamp-2">{s.description}</p>}</>)}
    fields={[
      ["title", "Title", "text", { required: true }],
      ["kind", "Kind", "select", { options: SPECIAL_KINDS }],
      ["price", "Price (optional)", "text", { placeholder: "$5" }],
      ["day_of_week", "Day (optional)", "text", { placeholder: "Wednesdays" }],
      ["description", "Description", "textarea"],
      ["order", "Display order", "number"],
    ]}
  />;
}

// ============== SITE SETTINGS ==============
const SETTING_KEYS = [
  { key: "hero_btn1_text", label: "Hero button #1 text", hint: "Default: 'See This Week's Lineup'" },
  { key: "hero_btn1_url", label: "Hero button #1 link", hint: "Default: /events" },
  { key: "hero_btn1_hidden", label: "Hide hero button #1?", hint: "Type 'yes' to hide. Leave blank to show." },
  { key: "hero_btn2_text", label: "Hero button #2 text", hint: "Default: 'View Menu'" },
  { key: "hero_btn2_url", label: "Hero button #2 link", hint: "Default: /menu" },
  { key: "hero_btn2_hidden", label: "Hide hero button #2?", hint: "Type 'yes' to hide. Leave blank to show." },
  { key: "weekly_heading", label: "This Week heading", hint: "Default: 'THIS WEEK AT TNT'" },
  { key: "weekly_subheading", label: "This Week subheading", hint: "Default: 'Something happening every night.'" },
  { key: "food_heading", label: "Food section heading", hint: "Default: 'EAT, DRINK & STAY LATE'" },
  { key: "food_subheading", label: "Food section subheading", hint: "Default: 'Full kitchen open until close…'" },
  { key: "host_heading", label: "Host event heading", hint: "Default: 'HOST YOUR NEXT EVENT AT TAPS & TUNES'" },
  { key: "host_subheading", label: "Host event subheading", hint: "Default: 'From birthdays and bachelorette parties…'" },
];

export function SettingsManager() {
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [savedKey, setSavedKey] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/api/settings");
        setValues(data || {});
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async (key) => {
    setSaving((s) => ({ ...s, [key]: true }));
    try {
      await api.put(`/api/admin/settings/${key}`, { value: values[key] || "" });
      setSavedKey(key);
      setTimeout(() => setSavedKey(""), 1500);
    } catch (err) {
      alert(apiErrorMessage(err));
    } finally {
      setSaving((s) => ({ ...s, [key]: false }));
    }
  };

  if (loading) return <Loading />;

  return (
    <section data-testid="admin-settings" className="space-y-4">
      <div>
        <h2 className="font-display text-xl text-tnt-ink tracking-tight">Site Settings</h2>
        <p className="text-tnt-ink/55 text-xs mt-1">Override any of the defaults below. Leave blank to keep the default.</p>
      </div>
      <ul className="space-y-3">
        {SETTING_KEYS.map((s) => (
          <li key={s.key} className="bg-white border border-tnt-border rounded-lg p-4">
            <Field label={s.label} hint={s.hint}>
              <div className="flex gap-2">
                <input
                  type="text"
                  data-testid={`setting-${s.key}`}
                  value={values[s.key] || ""}
                  onChange={(e) => setValues((v) => ({ ...v, [s.key]: e.target.value }))}
                  className="form-input flex-1"
                />
                <button type="button" onClick={() => save(s.key)} disabled={saving[s.key]} className="btn-gold !py-2 !px-3 !text-xs">
                  {saving[s.key] ? <Loader2 className="w-4 h-4 animate-spin" /> : savedKey === s.key ? "Saved!" : "Save"}
                </button>
              </div>
            </Field>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ============== Generic CRUD list+editor ==============
function Crud({ endpoint, label, blurb, blankItem, renderCard, fields }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(endpoint);
      setItems(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Build the admin write path from the public list endpoint. Callers pass
  // e.g. "/api/menu-items" for the GET — turn that into "/api/admin/menu-items"
  // for POST/PUT/DELETE.
  const adminPath = endpoint.startsWith("/api/")
    ? `/api/admin/${endpoint.slice(5)}`
    : `/api/admin${endpoint}`;

  const remove = async (id) => {
    if (!window.confirm("Delete this item? This cannot be undone.")) return;
    try {
      await api.delete(`${adminPath}/${id}`);
      setItems((arr) => arr.filter((e) => e.id !== id));
    } catch (err) {
      alert(apiErrorMessage(err));
    }
  };

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl text-tnt-ink tracking-tight">{label} ({items.length})</h2>
        <button type="button" onClick={() => setEditing({ ...blankItem, id: null })} className="btn-gold !py-2 !px-4 !text-sm">
          <Plus className="w-4 h-4 mr-1.5" /> New
        </button>
      </div>
      {blurb && <p className="text-tnt-ink/55 text-xs">{blurb}</p>}

      {loading ? <Loading /> : items.length === 0 ? (
        <EmptyState title="Nothing here yet" subtitle="Click 'New' to add the first item." />
      ) : (
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it) => (
            <li key={it.id} className="bg-white border border-tnt-border rounded-lg overflow-hidden shadow-sm">
              {it.image_url && (
                <img src={imageUrl(it.image_url)} alt="" loading="lazy" className="w-full aspect-[4/3] object-cover bg-tnt-surface2" />
              )}
              <div className="p-4">
                {renderCard(it)}
                <div className="mt-3 flex gap-2">
                  <button type="button" onClick={() => setEditing(it)} className="inline-flex items-center gap-1 text-tnt-ink hover:text-tnt-gold text-xs font-display tracking-widest uppercase"><Pencil className="w-3 h-3" /> Edit</button>
                  <button type="button" onClick={() => remove(it.id)} className="ml-auto inline-flex items-center gap-1 text-red-600 hover:text-red-700 text-xs font-display tracking-widest uppercase"><Trash2 className="w-3 h-3" /> Delete</button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <Editor
          endpoint={endpoint}
          item={editing}
          fields={fields}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </section>
  );
}

function Editor({ endpoint, item, fields, onClose, onSaved }) {
  const [state, setState] = useState({ ...item });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const isEdit = Boolean(item.id);

  const adminPath = endpoint.startsWith("/api/")
    ? `/api/admin/${endpoint.slice(5)}`
    : `/api/admin${endpoint}`;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = { ...state };
      delete payload.id;
      // Coerce order to number
      if ("order" in payload) payload.order = Number(payload.order) || 0;
      if (isEdit) {
        await api.put(`${adminPath}/${item.id}`, payload);
      } else {
        await api.post(adminPath, payload);
      }
      onSaved();
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={isEdit ? "Edit" : "New"} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        {fields.map(([key, label, type, opts = {}]) => (
          <Field key={key} label={label} hint={opts.hint}>
            {type === "text" && (
              <input type="text" required={opts.required} placeholder={opts.placeholder} value={state[key] || ""} onChange={(e) => setState({ ...state, [key]: e.target.value })} className="form-input" />
            )}
            {type === "number" && (
              <input type="number" value={state[key] ?? 0} onChange={(e) => setState({ ...state, [key]: e.target.value })} className="form-input w-32" />
            )}
            {type === "textarea" && (
              <textarea rows={3} value={state[key] || ""} onChange={(e) => setState({ ...state, [key]: e.target.value })} className="form-input resize-none" />
            )}
            {type === "select" && (
              <select value={state[key] || ""} onChange={(e) => setState({ ...state, [key]: e.target.value })} className="form-input">
                {opts.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            )}
            {type === "image" && (
              <ImagePicker imageUrlState={state[key] || ""} setImageUrlState={(url) => setState({ ...state, [key]: url })} />
            )}
            {type === "checkbox" && (
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid={`form-${key}`}
                  checked={state[key] !== false}
                  onChange={(e) => setState({ ...state, [key]: e.target.checked })}
                  className="w-4 h-4 accent-tnt-gold"
                />
                <span className="text-sm text-tnt-ink/80">{opts.checkboxLabel || "Enabled"}</span>
              </label>
            )}
          </Field>
        ))}
        {error && <FormError>{error}</FormError>}
        <FormActions onCancel={onClose} saving={saving} />
      </form>
    </Modal>
  );
}
